import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { fetchPlacements } from 'core/actions/ocapi';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import BonusOffer from 'core/modules/BonusOffers/components/BonusOffer';
import List from 'core/modules/Partials/List/List';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';

import { useGetBonusOffers } from 'core/modules/BonusOffers/hooks';
import { useTriggerBonusOffers300x250GAEvents } from 'core/modules/BonusOffers300x250/hooks';

import { selectActiveMQ, selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import { selectSiteUrl } from 'core/selectors/app';
import {
  selectPopularOffersConfig, selectPopularOffersIsLoading, selectPopularOffersIsLoaded,
  selectPopularOffersPlacementsToDispay, selectBonusOffers,
} from './selectors';

import { triggerPopularOffersIsViewed } from './actions';

import PopularOffer from './components/PopularOffer/PopularOffer';

import './PopularOffers.scss';

function PopularOffers() {
  const dispatch = useDispatch();

  const activeMQ = useSelector(selectActiveMQ);
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const config = useSelector(selectPopularOffersConfig);
  const isLoading = useSelector(selectPopularOffersIsLoading);
  const isLoaded = useSelector(selectPopularOffersIsLoaded);
  const offersToDisplay = useSelector(selectPopularOffersPlacementsToDispay);
  const bonusOffers = useSelector(selectBonusOffers);
  const siteUrl = useSelector(selectSiteUrl);

  const {
    api: { params, options },
    carousel, sectionTitle, checkShouldUseCarousel, shouldIncludeBonusOffers, getShouldShowViewAllLink, tagHelper,
    includeBonusOffersOnMediaQueriesOnly, maxNumberOfBonusOffersInXXL,
  } = config;

  const checkShouldIncludeBonusOffers = () => {
    let includeOnMediaQueries = includeBonusOffersOnMediaQueriesOnly;
    if (maxNumberOfBonusOffersInXXL && bonusOffers.length <= maxNumberOfBonusOffersInXXL) {
      includeOnMediaQueries = [...includeBonusOffersOnMediaQueriesOnly, 'XXL'];
    }
    return shouldIncludeBonusOffers || includeOnMediaQueries.includes(activeMQ);
  };

  const allOffersLength = offersToDisplay.length + bonusOffers.length;

  const isSectionWithBonuOffers = checkShouldIncludeBonusOffers();
  const shouldUseCarousel = checkShouldUseCarousel(activeMQ, allOffersLength);

  useGetBonusOffers();
  useTriggerBonusOffers300x250GAEvents(isSectionWithBonuOffers);

  const handleRef = useCallback((ref) => {
    tagHelper(ref); // handle Chase tagging implementation
  }, []);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      dispatch(fetchPlacements(params, options));
    }
  });

  useEffect(() => {
    if (isLoaded && offersToDisplay.length) {
      dispatch(triggerPopularOffersIsViewed(offersToDisplay, offersToDisplay.length));
    }
  }, [isLoaded, offersToDisplay, dispatch]);

  // Make sure we have something to display
  if (isEmpty(offersToDisplay) && isEmpty(bonusOffers)) {
    return null;
  }

  const renderPopularOffers = () => (
    offersToDisplay.map(offer => (
      <PopularOffer data-test="popular-offer" {...offer} key={`${offer.id}`} config={config} />
    ))
  );

  const renderBonusOffers = () => (
    bonusOffers.map(offer => (
      <div className="mn_offerWrap mn_bonusOffer" key={offer.bonusId}>
        <BonusOffer className="mn_bonusMerchantOffer300x250" {...offer} />
      </div>
    ))
  );

  const shouldShowViewAllLink = getShouldShowViewAllLink(isMobileMQ);

  return (
    <div
      className="mn_popularOffers"
      data-item-count={allOffersLength}
      data-test="popular-offers"
      ref={handleRef}
    >
      <ContentWrap mobile tablet l xl>
        <SectionTitle title={sectionTitle}>
          {shouldShowViewAllLink && <a className="mn_viewAllLink" href={`${siteUrl}/sp____.htm`}> View all offers</a>}
        </SectionTitle>
        <List options={carousel} shouldUseCarousel={shouldUseCarousel}>
          {isSectionWithBonuOffers && renderBonusOffers()}
          {renderPopularOffers()}
        </List>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><PopularOffers {...props} /></ComponentErrorBoundary>;
